/** Brain ▸ Sessions — web mirror of `Sources/Views/SessionsView.swift`.
 *
 * The live tmux sessions on the operator's own Mac, read through
 * `AppEnvironment.sessions` (MockSessionsStore offline; BrainSessionsStore against
 * the Worker's OWNER-ONLY `GET /tmux` in the live path).
 *
 * The live call dispatches to the Mac bridge and can take up to ~25s or fail
 * (owner-only 403 / engine-offline 502·503·504), so this owns its own load state
 * with a clear "contacting your engine" indicator and a refresh button. */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useEnv } from '../app/context';
import { brainFsKind, type TmuxSession } from '../models/brainFs';
import { EmptyState, relativeShort, Section, StatusPill } from '../components/UIHelpers';

export function SessionsView() {
  const env = useEnv();
  const [sessions, setSessions] = useState<TmuxSession[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const reqId = useRef(0);
  const hasLoadedOnce = useRef(false);

  const load = useCallback(async () => {
    const myReq = ++reqId.current;
    setIsLoading(true);
    setError(null);
    try {
      const result = await env.sessions.list();
      if (myReq === reqId.current) setSessions(result.sessions);
    } catch (e) {
      if (myReq === reqId.current) {
        setError(e);
        setSessions([]);
      }
    } finally {
      if (myReq === reqId.current) {
        setIsLoading(false);
        hasLoadedOnce.current = true;
      }
    }
  }, [env]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <>
      <div className="fs-pathbar">
        <div className="fs-pathbar__row">
          <span aria-hidden>🖥</span>
          <span className="fs-pathbar__path">tmux sessions</span>
          <button className="fs-btn" onClick={() => void load()} disabled={isLoading} title="Refresh">
            ⟳ Refresh
          </button>
        </div>
      </div>

      {isLoading && sessions.length === 0 ? (
        <LoadingState />
      ) : error && sessions.length === 0 ? (
        <SessionsErrorState error={error} onRetry={() => void load()} />
      ) : sessions.length === 0 ? (
        <>
          <EmptyState icon="🖥" title="No sessions" message="No tmux sessions are running on your engine." />
          <div style={{ textAlign: 'center' }}>
            <button className="fs-btn" onClick={() => void load()}>
              Refresh
            </button>
          </div>
        </>
      ) : (
        <Section header={`${sessions.length} session${sessions.length === 1 ? '' : 's'}`}>
          {sessions.map((s) => (
            <SessionRow key={s.name} session={s} />
          ))}
        </Section>
      )}
      <div style={{ height: 24 }} />
    </>
  );
}

function SessionRow({ session: s }: { session: TmuxSession }) {
  return (
    <div className="row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 5 }}>
      <div className="row__head">
        <span aria-hidden style={{ width: 22, textAlign: 'center' }}>
          🖥
        </span>
        <div className="row__title" style={{ flex: 1, minWidth: 0, fontSize: 14 }}>
          {s.name}
        </div>
        {s.attached ? <StatusPill text="attached" color="var(--green)" icon="●" /> : null}
        <span className="caption">{relativeShort(s.createdAt)}</span>
      </div>
      <div className="caption" style={{ paddingLeft: 30, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
        <span>
          🪟 {s.windows ?? 0} window{(s.windows ?? 0) === 1 ? '' : 's'}
        </span>
        {s.paneCurrentCommand ? <span className="mono">⌘ {s.paneCurrentCommand}</span> : null}
      </div>
      {s.paneCurrentPath ? (
        <div className="caption mono" style={{ paddingLeft: 30, overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {s.paneCurrentPath}
        </div>
      ) : null}
    </div>
  );
}

function LoadingState() {
  return (
    <div className="empty">
      <div className="spinner" />
      <div className="empty__title" style={{ marginTop: 12 }}>
        Contacting your engine…
      </div>
      <div className="caption" style={{ marginTop: 4 }}>
        This can take up to ~25 seconds.
      </div>
    </div>
  );
}

/** Owner-only (403) and engine-offline (502·503·504) get dedicated copy. */
function SessionsErrorState({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  switch (brainFsKind(error)) {
    case 'ownerOnly':
      return (
        <EmptyState
          icon="🔒"
          title="Owner only"
          message="Sessions are only available on your own engine (owner only)."
        />
      );
    case 'engineOffline':
      return (
        <>
          <EmptyState icon="⚡︎" title="Engine offline" message="Engine offline — start your engine to see sessions." />
          <div style={{ textAlign: 'center' }}>
            <button className="fs-btn" onClick={onRetry}>
              Try again
            </button>
          </div>
        </>
      );
    case 'notSignedIn':
      return <EmptyState icon="🔑" title="Not signed in" message="Sign in to see sessions on your engine." />;
    default:
      return (
        <>
          <EmptyState
            icon="⚠"
            title="Couldn't load sessions"
            message={error instanceof Error ? error.message : String(error)}
          />
          <div style={{ textAlign: 'center' }}>
            <button className="fs-btn" onClick={onRetry}>
              Retry
            </button>
          </div>
        </>
      );
  }
}
